<template>
  <div class="app-showcase-process-step__one">
    <div class="container-fluid container-fluid--cp-150">
      <div class="row align-items-center">
        <div class="col-lg-5 col-md-5">
          <div class="app-showcase-process-step__media text-center wow move-up">
            <img :src="img" class="img-fluid" alt="thumbnail" />
          </div>
        </div>
        <div class="col-lg-7 col-md-7">
          <div class="app-showcase-process-step__content section-space--mt_40">
            <div class="app-showcase__inner">
              <h4 class="font-weight--light wow move-up">{{ title }}</h4>

              <!-- ready to connect wallet -->
              <div
                v-if="
                  walletAddress === null && connectedWalletAddress === null && !this.loadingWallet
                "
              >
                <div class="sub-text section-space--mb_40 wow move-up">
                  A Pixel Profile is required in order to play some Pixel games. In order to access
                  all features of our games, you must associate a Tezos wallet. <br /><br />
                  Don't have a wallet? Create one easily at
                  <a
                    class="hover-style-link hover-style-link--green"
                    href="https://templewallet.com/"
                    >https://templewallet.com/</a
                  >
                </div>

                <div class="wow move-up">
                  <div class="btn-sowcase section-space--mb_40 wow move-up">
                    <b-button @click="connectWallet" variant="primary" class="scroll btn btn--green"
                      >Connect Wallet</b-button
                    >
                  </div>
                </div>
              </div>
              <!-- loading wallet -->
              <div v-if="this.loadingWallet" class="d-flex align-items-left">
                <strong>Waiting for response from your wallet...</strong>
                <div class="spinner-border ml-2" role="status" aria-hidden="true"></div>
              </div>

              <!-- ready to associate wallet -->
              <div
                v-if="
                  walletAddress === null &&
                  connectedWalletAddress !== null &&
                  !this.loadingWallet &&
                  !this.loading
                "
              >
                <div class="sub-text section-space--mb_40 wow move-up">
                  Warning: Once this wallet address is associated with your profile, it cannot be
                  reversed. Only NFTs associated with this wallet address can be used in Pixel
                  games.

                  <br />
                  <i> Please approve one signing request and one transaction. </i>
                </div>

                <div class="btn-sowcase section-space--mb_40 wow move-up">
                  <b-button @click="associateWallet" variant="primary" class="scroll btn btn--green"
                    >Associate Wallet & Complete Profile</b-button
                  >
                </div>
                <div class="btn-sowcase section-space--mb_40 wow move-up">
                  <b-button @click="disconnectWallet" class="ht-btn-sm ht-btn" size="sm"
                    >Disconnect Wallet</b-button
                  >
                </div>
              </div>
            </div>

            <div v-if="this.loading" class="d-flex align-items-left">
              <strong>Please wait up to 1 min for block confirmation</strong>
              <div class="spinner-border ml-2" role="status" aria-hidden="true"></div>
            </div>
          </div>
        </div>
      </div>
      <TermsLogsModal />
    </div>
  </div>
</template>

<script>
import { SigningType } from '@airgap/beacon-sdk'
import { packDataBytes, Parser } from '@taquito/michel-codec'
import { tzip16 } from '@taquito/tzip16'
import axios from 'axios'

import TermsLogsModal from '../components/modals/TermsLogsModal'
import { handleGlobalError } from '../util'

export default {
  name: 'AssociateWallet',
  components: {
    TermsLogsModal,
  },
  data() {
    return {
      img: require('../assets/img/hero/startup/top.png'),
      title: 'The fun begins with a Pixel Profile!',
      desc: '',
      associated: 'Wallet association success!',
      form: {
        email: '',
      },
      show: true,
      success: false,
      loading: false,
      loadingWallet: false,
    }
  },
  async mounted() {
    if (this.user === null) {
      this.$router.push('/login')
    }

    // check if we are in the middle of associating the wallet - wait until after computed values are set
    setTimeout(() => {
      if (this.connectedWalletAddress && this.user && this.user.walletAddress === null) {
        this.startCheckingHasWallet()
      }
    }, 1000)

    if (this.connectedWalletAddress && this.user && this.user.walletAddress) {
      this.$router.push('/profile')
    }
  },

  computed: {
    tezos() {
      return this.$store.getters.tezos
    },
    wallet() {
      return this.$store.getters.wallet
    },
    connectedWalletAddress() {
      return this.$store.getters.connectedWalletAddress
    },
    user() {
      return this.$store.getters.user
    },
    walletAddress() {
      return this.user ? this.user.walletAddress : null
    },
  },

  methods: {
    async connectWallet() {
      this.loadingWallet = true
      try {
        await this.wallet.requestPermissions({
          network: {
            type: process.env.VUE_APP_TEZOS_NETWORK,
            rpcUrl: process.env.VUE_APP_TEZOS_RPC,
          },
        })
        const connectedWalletAddress = await this.wallet.getPKH()
        this.tezos.setProvider({ wallet: this.wallet })

        this.$store.commit('initializeConnectedWalletAddress', connectedWalletAddress)

        this.loadingWallet = false
      } catch (error) {
        this.loadingWallet = false
        console.error('Unable to connect wallet', error)
        handleGlobalError(this, error, 'connecting wallet')
      }
    },
    async associateWallet() {
      this.loadingWallet = true
      try {
        const otpResp = await axios.post(`${process.env.VUE_APP_PXL_API}/request-otp`, {
          walletAddress: this.connectedWalletAddress,
        })

        const { address, publicKey } = await this.wallet.client.getActiveAccount()

        const contract = await this.tezos.wallet.at(process.env.VUE_APP_PP_SIGNATORY, tzip16)
        const storage = await contract.storage()

        const value = otpResp.data.code
        const counter = storage.toNumber()

        const data = `(Pair ${counter} "${value}")`
        const type = `(pair int string)`
        // We first use the `Parser` class and its `parseMichelineExpression` method to transform the Michelson data and type into their JSON representation
        const p = new Parser()
        const dataJSON = p.parseMichelineExpression(data)
        const typeJSON = p.parseMichelineExpression(type)

        const packed = packDataBytes(dataJSON, typeJSON)

        // The signing
        // The payload to send to the wallet
        const payload = {
          signingType: SigningType.MICHELINE,
          payload: packed.bytes,
          sourceAddress: address,
        }

        // The signing
        const signedPayload = await this.wallet.client.requestSignPayload(payload)

        // The signature
        const { signature } = signedPayload
        const op = await contract.methods.default(publicKey, signature, value).send()
        this.loadingWallet = false
        this.loading = true
        await op.confirmation(1, 5)

        this.startCheckingHasWallet()
      } catch (error) {
        this.loadingWallet = false
        console.error('Unable to associate wallet', error)
        handleGlobalError(this, error, 'associating wallet to profile')
      }
    },
    async disconnectWallet() {
      await this.wallet.client.clearActiveAccount()
      this.$store.commit('initializeConnectedWalletAddress', null)
      this.stopCheckingHasWallet()
    },
    /**
     * start regularly checking to see if the wallet has finished being associated
     */
    startCheckingHasWallet() {
      if (this.checkingWallet) {
        return
      }
      this.checkingWallet = setInterval(this.hasWallet, 5000)
    },
    stopCheckingHasWallet() {
      clearInterval(this.checkingWallet)
    },
    async hasWallet() {
      try {
        const resp = await axios.get(`${process.env.VUE_APP_PXL_API}/has-wallet`)
        this.loading = false
        if (resp && resp.data && resp.data.has_wallet) {
          this.successToast() // this does not display the toast because we navigate before it shows.  oh well
          // commit the wallet to the user and navigate to the profile
          this.$store.commit('updateUser', { walletAddress: this.connectedWalletAddress })
          this.stopCheckingHasWallet()
          this.$router.replace('/profile')
        } else {
          // do nothing
          console.info('wallet pending association')
        }
      } catch (err) {
        this.stopCheckingHasWallet()
        console.error(err)
      }
    },
    successToast() {
      this.$bvToast.toast('Wallet has been associated to your Pixel Profile.', {
        title: 'Success!',
        variant: 'success',
        solid: true,
        toaster: 'b-toaster-top-center',
      })
    },
  },
}
</script>
